import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowUpRight, Hourglass, Inbox, Info, Lock, RotateCw } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { moduleStyle } from './moduleStyle'

/**
 * The frame every widget renders in: the module's icon (its Home tile's), the
 * title with its description one hover away, the module it comes from
 * (linked), and a body. The body is either the widget's content or one of the
 * stated states below, so a widget never shows a blank box.
 */
export function WidgetCard({
  title,
  description,
  moduleId,
  moduleLabel,
  moduleTo,
  actions,
  children,
  className,
}: {
  title: string
  description?: string
  /** Header controls beside the module link, e.g. the view switcher. */
  actions?: ReactNode
  moduleId?: string
  moduleLabel: string
  moduleTo: string
  children: ReactNode
  className?: string
}) {
  const style = moduleId ? moduleStyle(moduleId) : null
  const Icon = style?.icon
  return (
    <section
      aria-label={title}
      className={cn(
        'flex h-full min-h-[148px] flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      <header className='mb-3 flex items-start gap-2.5'>
        {Icon && (
          <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', style.tile)} aria-hidden>
            <Icon className='h-3.5 w-3.5 text-white' />
          </span>
        )}
        <div className='flex min-w-0 flex-1 items-center gap-1'>
          <h3 className='truncate text-sm font-semibold text-gray-800'>{title}</h3>
          {description && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type='button' aria-label={`About ${title}`} className='shrink-0 rounded text-gray-300 hover:text-gray-500'>
                    <Info className='h-3.5 w-3.5' />
                  </button>
                </TooltipTrigger>
                <TooltipContent className='max-w-xs text-xs'>{description}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {actions}
        <Link
          to={moduleTo}
          title={`Open ${moduleLabel}`}
          className='flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-gray-400 hover:bg-gray-50 hover:text-blue-600'
        >
          {moduleLabel}
          <ArrowUpRight className='h-3 w-3' />
        </Link>
      </header>
      <div className='flex min-h-0 flex-1 flex-col'>{children}</div>
    </section>
  )
}

/**
 * Number colours. Values wear ink, not a series colour; only a number that is
 * itself a status (late, overdue) wears the status colour, and its caption
 * always says which.
 */
const STAT_TONE = {
  blue: 'text-gray-900',
  green: 'text-gray-900',
  purple: 'text-gray-900',
  amber: 'text-amber-600',
  red: 'text-red-600',
} as const

/**
 * One headline number with what it counts, then optionally its change against
 * the previous period, a day-by-day trend, and a line beneath. With `href`
 * the number opens the list it counts.
 */
export function WidgetStat({
  value,
  caption,
  tone = 'blue',
  delta,
  trend,
  footer,
  href,
}: {
  value: number | string
  caption: ReactNode
  tone?: keyof typeof STAT_TONE
  delta?: ReactNode
  trend?: ReactNode
  footer?: ReactNode
  href?: string
}) {
  const number = <span className={cn('text-3xl font-bold tabular-nums', STAT_TONE[tone])}>{value}</span>
  return (
    <div className='flex flex-1 flex-col justify-between gap-2'>
      <div>
        <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
          {href ? (
            <Link to={href} className='rounded hover:underline' title='Open the list'>
              {number}
            </Link>
          ) : (
            number
          )}
          {delta}
        </div>
        <p className='mt-0.5 text-xs text-gray-500'>{caption}</p>
      </div>
      {trend}
      {footer && <p className='text-[11px] text-gray-400'>{footer}</p>}
    </div>
  )
}

/** Several small labelled counts side by side, e.g. runs by status. */
export function WidgetStatGrid({
  items,
  footer,
}: {
  items: { label: string; value: number | string; tone?: keyof typeof STAT_TONE }[]
  footer?: ReactNode
}) {
  return (
    <div className='flex flex-1 flex-col justify-between gap-2'>
      <dl className='grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4'>
        {items.map(item => (
          <div key={item.label}>
            <dd className={cn('text-xl font-bold tabular-nums', STAT_TONE[item.tone ?? 'blue'])}>{item.value}</dd>
            <dt className='text-[11px] text-gray-500'>{item.label}</dt>
          </div>
        ))}
      </dl>
      {footer && <p className='text-[11px] text-gray-400'>{footer}</p>}
    </div>
  )
}

function StateMessage({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className='flex flex-1 flex-col items-center justify-center gap-1.5 py-4 text-center text-xs text-gray-400'>
      {icon}
      {children}
    </div>
  )
}

export function WidgetLoading() {
  return (
    <div role='status' aria-label='Loading' className='flex flex-1 flex-col gap-2 py-1'>
      <div className='h-7 w-16 animate-pulse rounded bg-gray-100' />
      <div className='h-3 w-full animate-pulse rounded bg-gray-100' />
      <div className='h-3 w-2/3 animate-pulse rounded bg-gray-100' />
    </div>
  )
}

export function WidgetEmpty({ children = 'Nothing to show for this period.' }: { children?: ReactNode }) {
  return <StateMessage icon={<Inbox className='h-5 w-5' />}>{children}</StateMessage>
}

export function WidgetError({ onRetry }: { onRetry?: () => void }) {
  return (
    <StateMessage icon={<AlertTriangle className='h-5 w-5 text-amber-400' />}>
      <span>Couldn’t load this widget.</span>
      {onRetry && (
        <button
          type='button'
          onClick={onRetry}
          className='flex items-center gap-1 font-medium text-blue-600 hover:underline'
        >
          <RotateCw className='h-3 w-3' /> Try again
        </button>
      )}
    </StateMessage>
  )
}

/** The page is open to the role but the data behind this widget is not. */
export function WidgetForbidden() {
  return (
    <StateMessage icon={<Lock className='h-5 w-5' />}>
      You don’t have access to this data in this site.
    </StateMessage>
  )
}

/** Listed in the catalog, not built yet. */
export function WidgetUnavailable() {
  return (
    <StateMessage icon={<Hourglass className='h-5 w-5' />}>
      This widget isn’t available yet.
    </StateMessage>
  )
}

/**
 * Whether a failed request was refused rather than broken. The API clients
 * throw the backend's message, not the status, and Nest's guard answers a 403
 * with "Forbidden resource".
 */
export function isForbiddenError(error: unknown): boolean {
  return error instanceof Error && /forbidden/i.test(error.message)
}

/**
 * The usual wiring for a widget backed by one query: loading, refused, failed
 * and empty states first, the content only when there is something to show.
 */
export function WidgetQueryState({
  isLoading,
  error,
  isEmpty,
  onRetry,
  emptyText,
  children,
}: {
  isLoading: boolean
  error: unknown
  isEmpty: boolean
  onRetry?: () => void
  emptyText?: ReactNode
  children: ReactNode
}) {
  if (isLoading) return <WidgetLoading />
  if (error) return isForbiddenError(error) ? <WidgetForbidden /> : <WidgetError onRetry={onRetry} />
  if (isEmpty) return <WidgetEmpty>{emptyText}</WidgetEmpty>
  return <>{children}</>
}

/** Shown when a dashboard change could not be stored, so it is not lost silently. */
export function SaveFailedNote() {
  return (
    <p role='alert' className='mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700'>
      <AlertTriangle className='h-3.5 w-3.5 shrink-0' />
      Couldn’t save your changes. They will be lost when you refresh. Check your connection and try again.
    </p>
  )
}
